import { useState } from 'react'
import { CheckCircle2, Info, KeyRound, ShieldAlert } from 'lucide-react'
import { DateValue, ErrorState, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useScimSetup, useSsoConfiguration, useSsoMutations } from '../api'

/**
 * The identity provider, and the connector that provisions accounts from it.
 *
 * <p>Two switches on this page can lock a company out or let strangers in, so each states what
 * it will do before it is touched, and the dangerous one — turning password sign-in off —
 * is refused until the administrator has proved the configuration works on themselves.
 */
export function SsoPage() {
  useBreadcrumb()
  const configuration = useSsoConfiguration()
  const scim = useScimSetup()
  const actions = useSsoMutations()

  const [draft, setDraft] = useState<{
    displayName: string; issuer: string; clientId: string; clientSecret: string
    scopes: string; emailClaim: string; nameClaim: string; allowedEmailDomains: string
    jitProvisioning: boolean; enabled: boolean
  } | null>(null)
  const [banner, setBanner] = useState<string | null>(null)
  const [tested, setTested] = useState<string | null>(null)

  const current = configuration.data
  // Adjust during render: the form starts from whatever is stored, once it has arrived.
  if (current && draft === null) {
    setDraft({
      displayName: current.displayName ?? '',
      issuer: current.issuer ?? '',
      clientId: current.clientId ?? '',
      clientSecret: '',
      scopes: current.scopes,
      emailClaim: current.emailClaim,
      nameClaim: current.nameClaim,
      allowedEmailDomains: current.allowedEmailDomains ?? '',
      jitProvisioning: current.jitProvisioning,
      enabled: current.enabled,
    })
  }

  const run = (work: () => Promise<unknown>, fallback: string) => {
    void (async () => {
      setBanner(null)
      try {
        await work()
      } catch (caught) {
        setBanner(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Single sign-on"
        subtitle="Who signs people in, and who says which of them should have an account."
      />

      {banner && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {banner}
        </p>
      )}

      {configuration.isPending && <Skeleton className="h-64" />}
      {configuration.isError && (
        <ErrorState error={configuration.error} onRetry={() => void configuration.refetch()} />
      )}

      {current && draft && (
        <>
          <Card className="p-4">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <StatusBadge
                status={current.enabled ? 'ON' : 'OFF'}
                tone={current.enabled ? 'success' : 'neutral'}
                label={current.enabled ? 'Single sign-on is on' : 'Single sign-on is off'}
              />
              {current.lastTestedAt && (
                <span className="text-xs text-[var(--color-muted)]">
                  Last tested <DateValue value={current.lastTestedAt} withTime />
                </span>
              )}
            </div>

            <p className="mb-3 flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
              <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              OpenID Connect, with the authorization code flow and PKCE. SAML is deliberately not
              offered: doing it without a maintained XML signature stack produces something that
              looks like enterprise sign-on and is a login bypass.
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="sso-name">What to call it on the sign-in screen</Label>
                <Input
                  id="sso-name" value={draft.displayName} placeholder="Company single sign-on"
                  onChange={(event) => setDraft({ ...draft, displayName: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="sso-issuer">Issuer</Label>
                <Input
                  id="sso-issuer" value={draft.issuer}
                  placeholder="https://login.microsoftonline.com/<tenant>/v2.0"
                  onChange={(event) => setDraft({ ...draft, issuer: event.target.value })}
                />
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  Discovery is read from <code>{'<issuer>'}/.well-known/openid-configuration</code>.
                </p>
              </div>
              <div>
                <Label htmlFor="sso-client">Client id</Label>
                <Input
                  id="sso-client" value={draft.clientId}
                  onChange={(event) => setDraft({ ...draft, clientId: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="sso-secret">Client secret</Label>
                <Input
                  id="sso-secret" type="password" value={draft.clientSecret}
                  placeholder={current.hasClientSecret ? 'Stored. Leave blank to keep it.' : ''}
                  onChange={(event) => setDraft({ ...draft, clientSecret: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="sso-domains">Allowed email domains</Label>
                <Input
                  id="sso-domains" value={draft.allowedEmailDomains} placeholder="example.com, example.ae"
                  onChange={(event) => setDraft({ ...draft, allowedEmailDomains: event.target.value })}
                />
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  Blank accepts any domain the provider asserts, which on a large tenant is more
                  people than you mean.
                </p>
              </div>
              <div>
                <Label htmlFor="sso-email-claim">Email claim</Label>
                <Input
                  id="sso-email-claim" value={draft.emailClaim}
                  onChange={(event) => setDraft({ ...draft, emailClaim: event.target.value })}
                />
              </div>
            </div>

            <div className="mt-3 space-y-2">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox" className="mt-0.5 size-4" aria-label="Just-in-time provisioning"
                  checked={draft.jitProvisioning}
                  onChange={(event) => setDraft({ ...draft, jitProvisioning: event.target.checked })}
                />
                <span>
                  Create an account for anybody the provider authenticates
                  <span className="block text-xs text-[var(--color-muted)]">{current.jitNote}</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox" className="mt-0.5 size-4" aria-label="Enabled"
                  checked={draft.enabled}
                  onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
                />
                <span>
                  Offer single sign-on on the sign-in screen
                  <span className="block text-xs text-[var(--color-muted)]">
                    Switching this on needs a successful test first.
                  </span>
                </span>
              </label>
            </div>

            <div className="mt-3">
              <Label>Redirect URI to configure at the provider</Label>
              <code className="mt-1 block break-all rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-2 font-mono text-xs">
                {current.redirectUri}
              </code>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => run(async () => {
                  await actions.save.mutateAsync({
                    displayName: draft.displayName,
                    issuer: draft.issuer,
                    clientId: draft.clientId,
                    clientSecret: draft.clientSecret || undefined,
                    scopes: draft.scopes,
                    emailClaim: draft.emailClaim,
                    nameClaim: draft.nameClaim,
                    allowedEmailDomains: draft.allowedEmailDomains || null,
                    jitProvisioning: draft.jitProvisioning,
                    enabled: draft.enabled,
                  })
                  setDraft({ ...draft, clientSecret: '' })
                }, 'That could not be saved')}
              >
                Save
              </Button>
              <Button
                type="button" variant="outline"
                onClick={() => run(
                  async () => setTested(await actions.test.mutateAsync()),
                  'The provider could not be reached')}
              >
                <CheckCircle2 className="size-4" aria-hidden="true" />
                Test the connection
              </Button>
            </div>

            {(tested ?? current.lastTestResult) && (
              <p className="mt-3 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
                {tested ?? current.lastTestResult}
              </p>
            )}
          </Card>

          <Card className="p-4">
            <p className="flex items-center gap-2 text-sm font-medium text-[var(--color-heading)]">
              <ShieldAlert className="size-4" aria-hidden="true" />
              Password sign-in
            </p>
            <p className="mt-1 text-sm text-[var(--color-body)]">{current.passwordNote}</p>
            <Button
              type="button" variant="outline" className="mt-3"
              onClick={() => run(
                () => actions.setPasswordLogin.mutateAsync(!current.passwordLoginAllowed),
                'That could not be changed')}
            >
              <KeyRound className="size-4" aria-hidden="true" />
              {current.passwordLoginAllowed ? 'Turn password sign-in off' : 'Allow password sign-in'}
            </Button>
          </Card>
        </>
      )}

      <Card className="p-4">
        <p className="text-sm font-medium text-[var(--color-heading)]">
          SCIM provisioning
        </p>
        {scim.data && (
          <dl className="mt-2 space-y-2 text-sm">
            <div>
              <dt className="text-[var(--color-muted)]">Base URL</dt>
              <dd><code className="font-mono text-xs">{scim.data.baseUrl}</code></dd>
            </div>
            <div>
              <dt className="text-[var(--color-muted)]">Authentication</dt>
              <dd className="text-[var(--color-body)]">{scim.data.authentication}</dd>
            </div>
            <div>
              <dt className="text-[var(--color-muted)]">Filters</dt>
              <dd className="text-[var(--color-body)]">{scim.data.supportedFilters}</dd>
            </div>
            <div>
              <dt className="text-[var(--color-muted)]">Delete</dt>
              <dd className="text-[var(--color-body)]">{scim.data.deleteBehaviour}</dd>
            </div>
            <div>
              <dt className="text-[var(--color-muted)]">Scope</dt>
              <dd className="text-[var(--color-body)]">{scim.data.scopeNote}</dd>
            </div>
          </dl>
        )}
      </Card>
    </div>
  )
}
